import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

const VIDEOS = [
  { id: 1, src: "/media/gallery/demo1.mp4", poster: "/media/gallery/poster1.jpg" },
  { id: 2, src: "/media/gallery/demo2.mp4", poster: "/media/gallery/poster2.jpg" },
  { id: 3, src: "/media/gallery/demo3.mp4", poster: "/media/gallery/poster3.jpg" },
  { id: 4, src: "/media/gallery/demo4.mp4", poster: "/media/gallery/poster4.jpg" },
  { id: 5, src: "/media/gallery/demo5.mp4", poster: "/media/gallery/poster5.jpg" },
  { id: 6, src: "/media/gallery/demo6.mp4", poster: "/media/gallery/poster6.jpg" },
  { id: 7, src: "/media/gallery/demo7.mp4", poster: "/media/gallery/poster7.jpg" },
  { id: 8, src: "/media/gallery/demo8.mp4", poster: "/media/gallery/poster8.jpg" },
  { id: 9, src: "/media/gallery/demo9.mp4", poster: "/media/gallery/poster9.jpg" },
  { id: 10, src: "/media/gallery/demo10.mp4", poster: "/media/gallery/poster10.jpg" },
  { id: 11, src: "/media/gallery/demo11.mp4", poster: "/media/gallery/poster11.jpg" },
  { id: 12, src: "/media/gallery/demo12.mp4", poster: "/media/gallery/poster12.jpg" },
  { id: 13, src: "/media/gallery/demo13.mp4", poster: "/media/gallery/poster13.jpg" },
  { id: 14, src: "/media/gallery/demo14.mp4", poster: "/media/gallery/poster14.jpg" },
];

export default function Gallery() {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <main data-testid="gallery-page" className="bg-[#0a0a0a] text-[#f4ede0]">
      <Navbar onCartClick={() => (window.location.href = "/#configurateur")} />

      <section className="luxe-bg pt-32 pb-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#cbc1ad] hover:text-[#d89557] transition mb-10">
            <ArrowLeft className="w-4 h-4" /> {t("gallery.back")}
          </Link>

          <div className="text-center mb-14 sm:mb-20">
            <span className="text-xs uppercase tracking-[0.32em] text-[#d89557]">{t("gallery.label")}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f4ede0] mt-4 sm:mt-6 leading-[1.05]">
              {t("gallery.title1")} <span className="italic copper-text">{t("gallery.title2")}</span>
            </h1>
            <p className="text-[#cbc1ad] mt-6 max-w-xl mx-auto text-sm sm:text-base">{t("gallery.desc")}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#7a716a] border border-[#2a2422] px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#d89557]" />
              {t("gallery.protected")}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {VIDEOS.map((v, i) => (
              <button
                key={v.id}
                data-testid={`gallery-thumb-${v.id}`}
                onClick={() => setActive(v)}
                className="group relative aspect-[4/5] overflow-hidden lcd-frame text-left fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <img src={v.poster} alt={`Démonstration ${v.id}`} className="absolute inset-0 w-full h-full object-cover transition group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                <div className="absolute top-4 left-4 px-2.5 py-1 border border-[#b87333]/50 bg-[#0a0a0a]/70 backdrop-blur text-[10px] uppercase tracking-[0.3em] text-[#d89557]">
                  N°{String(v.id).padStart(2, "0")}
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <div className="w-16 h-16 rounded-full bg-[#b87333]/20 backdrop-blur border border-[#d89557] flex items-center justify-center">
                    <Play className="w-6 h-6 text-[#f3c98a] ml-0.5" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 font-display italic text-lg text-[#f4ede0]">
                  {t(`gallery.captions.${v.id}`)}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Modal video player */}
      {active && (
        <div
          data-testid="gallery-modal"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
          style={{ animationDuration: "0.3s" }}
        >
          <button
            onClick={() => setActive(null)}
            className="absolute top-6 right-6 text-[#cbc1ad] hover:text-[#d89557] text-sm uppercase tracking-[0.22em]"
            data-testid="gallery-close"
          >
            ✕ {t("gallery.close")}
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <video
              src={active.src}
              controls
              autoPlay
              loop
              playsInline
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              className="w-full max-h-[85vh] object-contain lcd-frame"
            />
            <p className="text-center text-[10px] uppercase tracking-[0.25em] text-[#7a716a] mt-4">
              {t("gallery.protected")}
            </p>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
}
